import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"
import {
  retrieveSelfTenant,
  type EvoConfig,
  type RetrieveSelfTenantResponse,
  type TenantAcquirer,
} from "@/services/retrieveSelfTenant"
import type { PartialUpdateTenant } from "@/services/settings/partialUpdateSettings"
import type { ClientCurrencyCode } from "@/services/sharedTypes/tenants"

export type VatSettings = {
  isVatEnabled: boolean
  vatNumber: string | null
  defaultVatRate: number
}
// Keep in sync with tenants.api.tenants.TenantAddressSchema
export type TenantAddress = {
  addressLine1: string
  addressLine2?: string
  city: string
  postcode: string
}

// Keep in sync with tenants.api.tenants.TenantAccessControlSchema
export type TenantAccessControl = {
  transactions: boolean
  reports: boolean
}

export type Tenant = {
  schemaName: string
  name: string
  merchantGroupName: string | null
  isDemo: boolean
  address: TenantAddress

  youlendOffer: number | null

  defaultVatRate: number
  defaultExportFiletype: FileExportFormat

  accessControlRestrict: TenantAccessControl

  isVatEnabled: boolean
  vatNumber: string

  isCashEnabled: boolean
  isTransactionReceiptsEnabled: boolean
  isReceiptQrCodeEnabled: boolean
  isGratuityEnabled: boolean
  isCardReceiptsEnabled: boolean
  isAutomaticMerchantReceiptEnabled: boolean

  isProductsEnabled: boolean
  isCashiersEnabled: boolean
  allowLoginWithoutCashier: boolean

  isServiceChargeEnabled: boolean
  defaultServiceChargePercentage: number
  defaultServiceChargeFlat: number

  isSplitPaymentsEnabled: boolean
  isOpenOrdersEnabled: boolean
  isTablesEnabled: boolean

  isAutomaticPrintingEnabled: boolean
  isHospitalityAvailable: boolean
  isHospitalityEnabled: boolean
  isCollectionEnabled: boolean
  isUnlinkedRefundsEnabled: boolean
  unlinkedRefundsSingleMaximum: number
  unlinkedRefundsDailyMaximum: number
  hasDonationsModule: boolean
  hasSalesModule: boolean
  currency: ClientCurrencyCode
  acquirer: TenantAcquirer
  evoConfig: EvoConfig
  isUKmerchant: boolean
}

const tenantState = reactive<Tenant>({
  schemaName: "",
  name: "",
  merchantGroupName: null,
  youlendOffer: null,
  isDemo: false,
  isVatEnabled: false,
  vatNumber: "",
  isAutomaticPrintingEnabled: false,
  isGratuityEnabled: false,
  isCashEnabled: false,
  isProductsEnabled: false,
  isCashiersEnabled: false,
  allowLoginWithoutCashier: false,
  address: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    postcode: "",
  },
  accessControlRestrict: {
    transactions: false,
    reports: false,
  },
  defaultVatRate: 0,
  defaultExportFiletype: "csv",
  isOpenOrdersEnabled: false,
  isTablesEnabled: false,
  isSplitPaymentsEnabled: false,
  isTransactionReceiptsEnabled: false,
  isReceiptQrCodeEnabled: false,
  isCardReceiptsEnabled: false,
  isAutomaticMerchantReceiptEnabled: false,
  isServiceChargeEnabled: false,
  defaultServiceChargePercentage: 0,
  defaultServiceChargeFlat: 0,
  isHospitalityAvailable: false,
  isHospitalityEnabled: false,
  isCollectionEnabled: false,

  isUnlinkedRefundsEnabled: false,
  unlinkedRefundsDailyMaximum: 0,
  unlinkedRefundsSingleMaximum: 0,

  hasDonationsModule: false,
  hasSalesModule: false,
  currency: "GBP",
  acquirer: "barclaycard-elavon",
  evoConfig: {
    gratuityIsAvailable: false,
  },
  isUKmerchant: true,
})

const tenantStateRefs = toRefs(tenantState)

const setTenantState = (
  responseTenantState: RetrieveSelfTenantResponse
) => {
  tenantState.merchantGroupName =
    responseTenantState.merchantGroupName
  tenantState.address = responseTenantState.address
  tenantState.youlendOffer =
    responseTenantState.youlendOffer
  tenantState.schemaName = responseTenantState.schemaName
  tenantState.isDemo = responseTenantState.isDemo
  tenantState.defaultExportFiletype =
    responseTenantState.defaultExportFiletype
  tenantState.isProductsEnabled =
    responseTenantState.isProductsEnabled
  tenantState.isCashiersEnabled =
    responseTenantState.isCashiersEnabled
  tenantState.allowLoginWithoutCashier =
    responseTenantState.allowLoginWithoutCashier
  tenantState.isVatEnabled =
    responseTenantState.isVatEnabled
  tenantState.name = responseTenantState.name
  tenantState.isGratuityEnabled =
    responseTenantState.isGratuityEnabled
  tenantState.isServiceChargeEnabled =
    responseTenantState.isServiceChargeEnabled
  tenantState.defaultServiceChargePercentage =
    responseTenantState.defaultServiceChargePercentage
  tenantState.defaultServiceChargeFlat =
    responseTenantState.defaultServiceChargeFlat
  tenantState.isCashEnabled =
    responseTenantState.isCashEnabled
  tenantState.accessControlRestrict = {
    transactions:
      responseTenantState.accessControlRestrictTransactions,
    reports:
      responseTenantState.accessControlRestrictReports,
  }
  tenantState.isTransactionReceiptsEnabled =
    responseTenantState.isTransactionReceiptsEnabled
  tenantState.isCardReceiptsEnabled =
    responseTenantState.isCardReceiptsEnabled
  tenantState.isReceiptQrCodeEnabled =
    responseTenantState.isReceiptQrCodeEnabled
  tenantState.isAutomaticMerchantReceiptEnabled =
    responseTenantState.isAutomaticMerchantReceiptEnabled
  tenantState.isOpenOrdersEnabled =
    responseTenantState.isOpenOrdersEnabled
  tenantState.isSplitPaymentsEnabled =
    responseTenantState.isSplitPaymentsEnabled
  tenantState.isTablesEnabled =
    responseTenantState.isTablesEnabled
  tenantState.vatNumber = responseTenantState.vatNumber
  tenantState.defaultVatRate =
    responseTenantState.defaultVatRate
  tenantState.isHospitalityAvailable =
    responseTenantState.isHospitalityAvailable
  tenantState.isHospitalityEnabled =
    responseTenantState.isHospitalityEnabled
  tenantState.isCollectionEnabled =
    responseTenantState.isCollectionEnabled

  tenantState.isUnlinkedRefundsEnabled =
    responseTenantState.isUnlinkedRefundsEnabled
  tenantState.unlinkedRefundsDailyMaximum =
    responseTenantState.unlinkedRefundsDailyMaximum
  tenantState.unlinkedRefundsSingleMaximum =
    responseTenantState.unlinkedRefundsSingleMaximum

  tenantState.hasDonationsModule =
    responseTenantState.hasDonationsModule
  tenantState.hasSalesModule =
    responseTenantState.hasSalesModule
  tenantState.currency = responseTenantState.currency
  tenantState.acquirer = responseTenantState.acquirer
  tenantState.evoConfig = responseTenantState.evoConfig
  tenantState.isUKmerchant = !!(
    !(import.meta.env.VITE_ORGANIZATION === "boipa") ||
    responseTenantState.merchantId
      ?.trim()
      .toUpperCase()
      .startsWith("GB")
  )
}

const vatSettings = computed(() => ({
  isVatEnabled: tenantState.isVatEnabled,
  vatNumber: tenantState.vatNumber,
  defaultVatRate: tenantState.defaultVatRate,
}))

const address = computed(() => tenantState.address)
const setAddress = (newAddress: TenantAddress) => {
  tenantState.address = newAddress
}

const setAccessControlRestrict = (
  newAccessControl: TenantAccessControl
) => {
  tenantState.accessControlRestrict = newAccessControl
}

const hasLoaded = ref(false)
const isLoading = ref(false)
const hydrateTenant = async () => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true

  try {
    const tenantResponse = await retrieveSelfTenant()

    setTenantState(tenantResponse)
    hasLoaded.value = true
  } finally {
    isLoading.value = false
  }
}

const resetTenant = () => {
  hasLoaded.value = false
}

const onUpdateSetting = async (
  params: PartialUpdateTenant
) => {
  try {
    const newState = await partialUpdateSettings(params)
    setTenantState(newState)
  } catch (error) {
    logError(error)
    toast.error("Failed to update setting")
  }
}

const anyHospitalityFeaturesEnabled = computed(
  () =>
    tenantState.isCollectionEnabled ||
    tenantState.isOpenOrdersEnabled ||
    tenantState.isTablesEnabled ||
    tenantState.isSplitPaymentsEnabled ||
    tenantState.isServiceChargeEnabled
)

const setUnlinkedRefundsSettings = (value: {
  isUnlinkedRefundsEnabled: boolean
  unlinkedRefundsSingleMaximum: number
  unlinkedRefundsDailyMaximum: number
}) => {
  tenantState.isUnlinkedRefundsEnabled =
    value.isUnlinkedRefundsEnabled
  tenantState.unlinkedRefundsSingleMaximum =
    value.unlinkedRefundsSingleMaximum
  tenantState.unlinkedRefundsDailyMaximum =
    value.unlinkedRefundsDailyMaximum
}

export const useTenant = () => {
  if (!hasLoaded.value) hydrateTenant()

  return {
    tenant: readonly(tenantState),
    isDemo: readonly(tenantStateRefs.isDemo),
    isProductsEnabled: readonly(
      tenantStateRefs.isProductsEnabled
    ),
    isCashiersEnabled: readonly(
      tenantStateRefs.isCashiersEnabled
    ),
    allowLoginWithoutCashier: readonly(
      tenantStateRefs.allowLoginWithoutCashier
    ),
    schemaName: readonly(tenantStateRefs.schemaName),
    name: readonly(tenantStateRefs.name),
    merchantGroupName: readonly(
      tenantStateRefs.merchantGroupName
    ),
    isVatEnabled: readonly(tenantStateRefs.isVatEnabled),
    accessControlRestrict: readonly(
      tenantStateRefs.accessControlRestrict
    ),
    isGratuityEnabled: readonly(
      tenantStateRefs.isGratuityEnabled
    ),
    isServiceChargeEnabled: readonly(
      tenantStateRefs.isServiceChargeEnabled
    ),

    isCashEnabled: readonly(tenantStateRefs.isCashEnabled),
    isTransactionReceiptsEnabled: readonly(
      tenantStateRefs.isTransactionReceiptsEnabled
    ),
    isCardReceiptsEnabled: readonly(
      tenantStateRefs.isCardReceiptsEnabled
    ),
    isReceiptQrCodeEnabled: readonly(
      tenantStateRefs.isReceiptQrCodeEnabled
    ),
    isOpenOrdersEnabled: readonly(
      tenantStateRefs.isOpenOrdersEnabled
    ),
    isTablesEnabled: readonly(
      tenantStateRefs.isTablesEnabled
    ),
    isSplitPaymentsEnabled: readonly(
      tenantStateRefs.isSplitPaymentsEnabled
    ),
    setTenantState,
    setAccessControlRestrict,
    defaultExportFiletype: readonly(
      tenantStateRefs.defaultExportFiletype
    ),
    isHospitalityAvailable: readonly(
      tenantStateRefs.isHospitalityAvailable
    ),
    isHospitalityEnabled: readonly(
      tenantStateRefs.isHospitalityEnabled
    ),
    address,
    setAddress,
    vatSettings,
    onUpdateSetting,
    anyHospitalityFeaturesEnabled,
    isCollectionEnabled: readonly(
      tenantStateRefs.isCollectionEnabled
    ),
    youlendOffer: readonly(tenantStateRefs.youlendOffer),
    isUnlinkedRefundsEnabled: readonly(
      tenantStateRefs.isUnlinkedRefundsEnabled
    ),
    unlinkedRefundsSingleMaximum: readonly(
      tenantStateRefs.unlinkedRefundsSingleMaximum
    ),
    unlinkedRefundsDailyMaximum: readonly(
      tenantStateRefs.unlinkedRefundsDailyMaximum
    ),

    hasDonationsModule: readonly(
      tenantStateRefs.hasDonationsModule
    ),
    hasSalesModule: readonly(
      tenantStateRefs.hasSalesModule
    ),
    currency: readonly(tenantStateRefs.currency),
    acquirer: readonly(tenantStateRefs.acquirer),
    evoConfig: readonly(tenantStateRefs.evoConfig),
    isUKmerchant: readonly(tenantStateRefs.isUKmerchant),

    setUnlinkedRefundsSettings,
    resetTenant,
  }
}
